import { useGetInvoicesSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

type InvoiceRow = {
  id: string;
  status: string;
  created: string;
};

const emptyValue = {
  ID: 'No Invoices Yet',
  Status: 'N/A',
  Created: 'N/A',
};

const schema: ColumnDefinition<InvoiceRow>[] = [
  {
    title: 'ID',
    key: 'id',
    colspan: 'col-span-2',
  },
  {
    title: 'Created',
    colspan: 'col-span-2',
    format: (row: InvoiceRow) => (
      <span>{new Date(row.created).toLocaleDateString()}</span>
    ),
  },
  {
    title: 'Status',
    key: 'status',
    justify: 'right',
    colspan: 'col-span-2',
  },
];

type Props = {
  workspaceId: string;
};

type StatusFilterValue = 'all' | 'draft' | 'active' | 'closed';

const InvoicesListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Invoices');

  const { data } = useGetInvoicesSuspense();
  const [, setLocation] = useLocation();
  const [selectedStatus, setSelectedStatus] =
    useState<StatusFilterValue>('all');

  const invoices: InvoiceRow[] = useMemo(
    () =>
      data.data.map((invoice) => ({
        id: invoice.id,
        status: invoice.status,
        created: invoice.created,
      })),
    [data]
  );

  // Filter invoices by selected status
  const filteredInvoices = useMemo(() => {
    if (selectedStatus === 'all') {
      return invoices;
    }
    return invoices.filter((invoice) => invoice.status === selectedStatus);
  }, [invoices, selectedStatus]);

  const getPath = useCallback(
    (invoice: InvoiceRow) =>
      makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreateInvoice = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'create-invoice'], {
        absolute: true,
      })
    );
  }, [workspaceId, setLocation]);

  // Define status filter options
  const statusFilterOptions = [
    { value: 'all' as const, label: 'All' },
    { value: 'active' as const, label: 'Active' },
    { value: 'closed' as const, label: 'Closed' },
    { value: 'draft' as const, label: 'Draft' },
  ];

  return (
    <PaymentViewLayout
      title="Invoices"
      primaryAction={{
        title: 'New',
        onClick: handleCreateInvoice,
        'data-testid': 'create-invoice-button',
      }}
    >
      <div className="flex flex-col min-h-f1 w-full h-full pt-f4">
        <div className="flex flex-row justify-between">
          <TabsV2
            options={statusFilterOptions}
            selectedValue={selectedStatus}
            onClick={setSelectedStatus}
          />
        </div>
        <NavTableV2
          textSize="md"
          data={filteredInvoices}
          emptyValue={emptyValue}
          getPath={getPath}
          schema={schema}
          showHeader={false}
          stickyHeader={false}
        />
      </div>
    </PaymentViewLayout>
  );
};

export const InvoicesListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<PaymentViewLoading message="Loading invoices..." />}>
    <InvoicesListPageContent workspaceId={workspaceId} />
  </Suspense>
);
